import { useEffect, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { formatLtcCostDisplay } from '@shared';
import { useLtcCostDisplayPrecision } from '@client/hooks/useLtcCostDisplayPrecision';

interface AnimatedCostDisplayProps {
  value: number;
  /** When true and LTC rounding is enabled, hover shows the precise (pre-step) dollar amount. */
  showPreciseTooltip?: boolean;
}

export function AnimatedCostDisplay({
  value,
  showPreciseTooltip = false,
}: AnimatedCostDisplayProps) {
  const roundingStep = useLtcCostDisplayPrecision();
  const [displayValue, setDisplayValue] = useState(value);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (value === displayValue) return;

    setIsTransitioning(true);

    const fadeOutTimer = setTimeout(() => {
      setDisplayValue(value);
      setIsTransitioning(false);
    }, 400);

    return () => clearTimeout(fadeOutTimer);
  }, [value]);

  const { formattedDisplay, formattedPrecise } = formatLtcCostDisplay(displayValue, {
    roundingStep,
  });

  const inner = (
    <span
      className="inline-block transition-all duration-400 ease-in-out"
      style={{
        opacity: isTransitioning ? 0 : 1,
        transform: isTransitioning ? 'translateY(4px)' : 'translateY(0)',
      }}
    >
      {formattedDisplay}
    </span>
  );

  if (showPreciseTooltip && roundingStep > 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent>
          <span>{formattedPrecise}</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return inner;
}
